import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { User } from '@/types/User';
import { diagnosticsLogStorage, OpenSyncLogRecord } from '@/store/idbStorage/diagnosticsLogStorage';
import {
  DIAGNOSTICS_CONSTANTS,
  SyncLogLevel,
  SyncLogOutcome,
} from '@/utils/diagnostics/diagnostics.constants';
import { decodeSessionIdFromToken } from '@/utils/diagnostics/jwtSession.util';
import {
  chunkConsolidatedSyncLog,
  chunkImmediateLog,
  normalizeLogRequestPayload,
} from '@/utils/diagnostics/logChunker.util';
import { drainPendingLogs, logOutbox } from '@/utils/diagnostics/logOutbox';
import { estimateSerializedSize, sanitizeLogData } from '@/utils/diagnostics/sanitizeLogData.util';

export type LogTransportContext = {
  apiUrl: string;
  token: string;
  user: User | null;
  sessionId: string | null;
  syncId: string | null;
};

let persistChain: Promise<void> = Promise.resolve();

function runPersist(task: () => Promise<void>): void {
  persistChain = persistChain.then(task).catch((error) => {
    console.error('[diagnostics] persistence task failed', error);
  });
}

function updateDrainContext(context: LogTransportContext): void {
  if (!context.apiUrl || !context.token) {
    return;
  }
  logOutbox.setDrainContext({
    apiUrl: context.apiUrl,
    token: context.token,
  });
}

async function enforceOpenSyncLimits(records: OpenSyncLogRecord[]): Promise<void> {
  const now = Date.now();
  const maxAge = DIAGNOSTICS_CONSTANTS.OPEN_SYNC_LOGS.MAX_AGE_MS;
  const sorted = [...records].sort((left, right) => left.startedAt.localeCompare(right.startedAt));
  for (const record of sorted) {
    const ageMs = now - Date.parse(record.startedAt);
    if (ageMs > maxAge) {
      await diagnosticsLogStorage.deleteOpenSyncLog(record.inspectionId);
    }
  }
  const remaining = await diagnosticsLogStorage.getAllOpenSyncLogs();
  while (remaining.length > DIAGNOSTICS_CONSTANTS.OPEN_SYNC_LOGS.MAX_INSPECTIONS) {
    const oldest = [...remaining].sort((left, right) =>
      left.startedAt.localeCompare(right.startedAt)
    )[0];
    await diagnosticsLogStorage.deleteOpenSyncLog(oldest.inspectionId);
    remaining.splice(
      remaining.findIndex((record) => record.inspectionId === oldest.inspectionId),
      1
    );
  }
}

async function bestEffortDirectPost(
  context: LogTransportContext,
  parts: ReturnType<typeof chunkImmediateLog>
): Promise<void> {
  if (!context.apiUrl || !context.token) {
    return;
  }
  for (const part of parts) {
    try {
      await fetch(`${context.apiUrl}/${API_ENDPOINTS.LOGS.LOGS}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${context.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(normalizeLogRequestPayload(part.payload)),
      });
    } catch (error) {
      console.error('[diagnostics] direct post failed', error);
    }
  }
}

export const logTransport = {
  buildContext(
    apiUrl: string,
    token: string,
    user: User | null,
    syncId: string | null
  ): LogTransportContext {
    try {
      return {
        apiUrl: apiUrl ?? '',
        token: token ?? '',
        user,
        sessionId: token ? decodeSessionIdFromToken(token) : null,
        syncId,
      };
    } catch {
      return {
        apiUrl: apiUrl ?? '',
        token: token ?? '',
        user,
        sessionId: null,
        syncId,
      };
    }
  },

  sendImmediate(
    context: LogTransportContext,
    message: string,
    level: SyncLogLevel,
    data: unknown
  ): void {
    try {
      console.log(message, data);
      if (!context.apiUrl || !context.token) {
        return;
      }
      updateDrainContext(context);
      const parts = chunkImmediateLog(
        message,
        level,
        data,
        context.user,
        context.sessionId,
        context.syncId
      );
      runPersist(async () => {
        try {
          await logOutbox.enqueueParts(parts, {
            sessionId: context.sessionId ?? 'unknown',
            syncId: context.syncId ?? 'unknown',
          });
        } catch {
          await bestEffortDirectPost(context, parts);
        }
      });
    } catch (error) {
      console.error('[diagnostics] sendImmediate failed', error);
    }
  },

  handoffConsolidated(
    context: LogTransportContext,
    inspectionId: string,
    input: Parameters<typeof chunkConsolidatedSyncLog>[0]
  ): void {
    try {
      updateDrainContext(context);
      const parts = chunkConsolidatedSyncLog(input);
      runPersist(async () => {
        try {
          await diagnosticsLogStorage.handoffToPendingLogs(inspectionId, parts, {
            sessionId: input.sessionId,
            syncId: input.syncId,
          });
          logOutbox.scheduleDrain(0);
        } catch (error) {
          console.error('[diagnostics] consolidated handoff failed', error);
          await bestEffortDirectPost(context, parts);
        }
      });
    } catch (error) {
      console.error('[diagnostics] handoffConsolidated failed', error);
    }
  },

  scheduleDrain(): void {
    try {
      logOutbox.scheduleDrain(0);
    } catch (error) {
      console.error('[diagnostics] scheduleDrain failed', error);
    }
  },

  setPaused(paused: boolean): void {
    try {
      logOutbox.setPaused(paused);
    } catch (error) {
      console.error('[diagnostics] setPaused failed', error);
    }
  },

  recoverDiagnostics(): void {
    void (async () => {
      try {
        const openRecords = await diagnosticsLogStorage.getAllOpenSyncLogs();
        await enforceOpenSyncLimits(openRecords);
        logOutbox.scheduleDrain(0);
        await drainPendingLogs();
      } catch (error) {
        console.error('[diagnostics] recovery failed', error);
      }
    })();
  },
};

export function createEmptyOpenSyncLogRecord(input: {
  inspectionId: string;
  syncId: string;
  sessionId: string;
  version: string;
}): OpenSyncLogRecord {
  const now = new Date().toISOString();
  return {
    inspectionId: input.inspectionId,
    syncId: input.syncId,
    sessionId: input.sessionId,
    version: input.version,
    startedAt: now,
    updatedAt: now,
    hasError: false,
    droppedEventCount: 0,
    byteSize: 0,
    events: [],
  };
}

export function appendEventToOpenRecord(
  record: OpenSyncLogRecord,
  level: SyncLogLevel,
  message: string,
  data: unknown
): OpenSyncLogRecord {
  try {
    const sanitized = sanitizeLogData(data);
    const event = {
      level,
      message,
      data: sanitized,
      at: new Date().toISOString(),
    };
    const nextEvents = [...record.events, event];
    let droppedEventCount = record.droppedEventCount;
    let events = nextEvents;
    if (events.length > DIAGNOSTICS_CONSTANTS.OPEN_SYNC_LOGS.MAX_EVENTS) {
      const overflow = events.length - DIAGNOSTICS_CONSTANTS.OPEN_SYNC_LOGS.MAX_EVENTS;
      droppedEventCount += overflow;
      events = events.slice(overflow);
    }
    let byteSize = estimateSerializedSize(events);
    while (
      byteSize > DIAGNOSTICS_CONSTANTS.OPEN_SYNC_LOGS.MAX_BYTES_PER_INSPECTION &&
      events.length > 1
    ) {
      events = events.slice(1);
      droppedEventCount += 1;
      byteSize = estimateSerializedSize(events);
    }
    const hasError = record.hasError || level === 'error';
    return {
      ...record,
      events,
      droppedEventCount,
      byteSize,
      hasError,
      updatedAt: new Date().toISOString(),
    };
  } catch {
    return {
      ...record,
      droppedEventCount: record.droppedEventCount + 1,
      updatedAt: new Date().toISOString(),
    };
  }
}

export type { SyncLogOutcome };
